import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Header from '../Component/Header'
import Footer from '../Component/footer'
import PageHead from '../Component/pageHead'
import { GetAction } from '../Component/GetAction'
import { PostAction } from '../Component/PostAction'
import { useFormik } from 'formik'
import {
  applyLoginResponse,
  loginPath,
  safeReturnPath,
} from '../utils/sessionAuth'

function normalizeIndianMobile(value) {
  let digits = String(value || '').replace(/\D/g, '')
  if (digits.length === 13 && digits.startsWith('091')) {
    digits = digits.slice(3)
  } else if (digits.length === 12 && digits.startsWith('91')) {
    digits = digits.slice(2)
  } else if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1)
  }
  return /^[6-9]\d{9}$/.test(digits) ? digits : ''
}

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  password: '',
  confirmPassword: '',
  adharCard: '',
  panNumber: '',
  address1: '',
  address2: '',
  city: '',
  country_id: '99',
  country_name: 'India',
  state: '',
  state_name: '',
  zip_no: '',
}

function SignUp() {
  const history = useHistory()
  const location = useLocation()
  const returnUrl = safeReturnPath(new URLSearchParams(location.search).get('returnUrl'))
  const [countryId, setCountryId] = useState('99')
  const [countryData, setCountryData] = useState([])
  const [listData, setListData] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem('id')) {
      history.replace(returnUrl || '/')
    }
  }, [history, returnUrl])

  useEffect(() => {
    const loadCountries = async () => {
      const res = await GetAction('get', {}, '/Master/countryList')
      setCountryData(Array.isArray(res?.response) ? res.response : [])
    }
    loadCountries()
  }, [])

  useEffect(() => {
    const loadStates = async () => {
      const res = await GetAction('get', { country_id: countryId }, '/Master/stateList')
      setListData(Array.isArray(res?.response) ? res.response : [])
    }
    loadStates()
  }, [countryId])

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: EMPTY,
    onSubmit: (submitValues) => {
      const firstName = String(submitValues.firstName || '').trim()
      const lastName = String(submitValues.lastName || '').trim()
      const email = String(submitValues.email || '').trim()
      const mobileRaw = String(submitValues.mobile || '').trim()
      const mobile = mobileRaw ? normalizeIndianMobile(mobileRaw) : ''
      const password = String(submitValues.password || '')
      const confirmPassword = String(submitValues.confirmPassword || '')

      if (!firstName || !lastName) {
        alert('Please enter your first and last name.')
        return
      }
      if (!email && !mobile) {
        alert('Please enter an email address or a mobile number.')
        return
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        alert('Please enter a valid email address.')
        return
      }
      if (mobileRaw && !mobile) {
        alert('Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.')
        return
      }
      if (!password) {
        alert('Please enter a password.')
        return
      }
      if (password !== confirmPassword) {
        alert('Password and confirm password do not match.')
        return
      }
      if (!submitValues.address1 || !submitValues.city || !submitValues.state || !submitValues.zip_no) {
        alert('Please complete your billing address.')
        return
      }

      const payload = {
        firstName,
        lastName,
        email,
        mobile,
        password,
        adharCard: submitValues.adharCard,
        panNumber: submitValues.panNumber,
        address1: submitValues.address1,
        address2: submitValues.address2,
        city: submitValues.city,
        country_id: submitValues.country_id,
        country_name: submitValues.country_name,
        state: submitValues.state,
        state_name: submitValues.state_name,
        zip_no: submitValues.zip_no,
      }

      const submit = async () => {
        setSaving(true)
        const res = await PostAction('post', payload, 'user/signupUser')
        if (res?.status !== 'success') {
          setSaving(false)
          alert(res?.message || res?.data?.message || 'Sign up failed.')
          return
        }

        const loginRes = await PostAction(
          'post',
          { username: email || mobile, password },
          'user/loginCheck'
        )
        setSaving(false)
        if (applyLoginResponse(loginRes)) {
          history.push(returnUrl || '/')
          return
        }
        alert('Account created. Please log in.')
        history.push(loginPath(returnUrl))
      }
      submit()
    },
  })

  const handleCountry = (event) => {
    const id = event.target.value
    const name = event.target[event.target.selectedIndex]?.getAttribute('data-name') || ''
    setCountryId(id)
    setFieldValue('country_id', id)
    setFieldValue('country_name', name)
    setFieldValue('state', '')
    setFieldValue('state_name', '')
  }

  const changeState = (event) => {
    const id = event.target.value
    const name = event.target[event.target.selectedIndex]?.getAttribute('data-name') || ''
    setFieldValue('state', id)
    setFieldValue('state_name', name)
  }

  return (
    <>
      <Header />
      <PageHead title="Sign Up" />
      <section className="donation-page">
        <div className="container">
          <div className="donation-card">
            <div className="donation-card-header">
              <h2 className="donation-card-title">Sign Up</h2>
              <p className="donation-card-subtitle">
                {returnUrl === '/Donation'
                  ? 'Create an account to continue your donation. Your donation details will be waiting after you sign up.'
                  : 'Create your account with the billing details below.'}
                {' '}Enter an email address, a mobile number, or both. At least one is required, and each can belong to only one account. A password reset is emailed when an email address is saved. Donation receipts stay with this account.
              </p>
            </div>

            <Form onSubmit={handleSubmit} className="donation-form">
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="firstName">
                    First name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="firstName"
                    id="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="lastName"
                    id="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    autoComplete="email"
                    placeholder="name@example.com"
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="mobile">
                    Mobile
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="mobile"
                    id="mobile"
                    value={values.mobile}
                    onChange={handleChange}
                    autoComplete="tel"
                    inputMode="numeric"
                    placeholder="10-digit mobile"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="password">
                    Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    id="password"
                    value={values.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="confirmPassword">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    id="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="adharCard">
                    Aadhaar Card Number <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="adharCard"
                    id="adharCard"
                    value={values.adharCard}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="panNumber">
                    PAN Number <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="panNumber"
                    id="panNumber"
                    value={values.panNumber}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="address1">
                  Address
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="address1"
                  id="address1"
                  value={values.address1}
                  onChange={handleChange}
                />
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="address2">
                    Address 2 <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="address2"
                    name="address2"
                    value={values.address2}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="city">
                    City
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    id="city"
                    value={values.city}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-5 mb-3">
                  <label className="form-label" htmlFor="country">
                    Country
                  </label>
                  <select
                    className="form-control"
                    id="country"
                    name="country_id"
                    onChange={(e) => {
                      handleCountry(e)
                      handleChange(e)
                    }}
                    value={values.country_id}
                  >
                    {countryData.map((country, index) => (
                      <option key={index} value={country.country_id} data-name={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label className="form-label" htmlFor="state">
                    State
                  </label>
                  <select
                    className="form-control"
                    id="state"
                    name="state"
                    onChange={(e) => {
                      changeState(e)
                      handleChange(e)
                    }}
                    value={values.state}
                  >
                    <option value="">Select state</option>
                    {listData.map((state, index) => (
                      <option key={index} value={state.zone_id} data-name={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label" htmlFor="zip">
                    Zip
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="zip_no"
                    id="zip"
                    value={values.zip_no}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="donation-actions">
                <button className="btn donation-submit-btn" type="submit" disabled={saving}>
                  {saving ? 'Creating account…' : 'Sign Up'}
                </button>
                <p className="text-center mt-3 mb-0" style={{ fontSize: 15 }}>
                  Already have an account?{' '}
                  <Link to={loginPath(returnUrl)}>Login</Link>
                </p>
              </div>
            </Form>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default SignUp
